// `createRouteMatcher` — the inverse of `buildUrl`: a request's verb + pathname back to the
// registry entry that declares it (BL-2343, ADR 066 — Offline Mode § 3). The admin phone
// app's offline transport answers typed-client calls by registry key, and only sees URLs.

import { flattenRegistry } from './define-route';
import type { HttpVerb, RegistryTree } from './types';

/** A matched request: the entry's dotted key, the verb, and the decoded path params. */
export interface RouteMatch {
  key: string;
  verb: HttpVerb;
  params: Record<string, string>;
}

type Segment = { kind: 'static'; value: string } | { kind: 'param'; name: string } | { kind: 'rest'; name: string };

interface CompiledRoute {
  key: string;
  verbs: ReadonlySet<HttpVerb>;
  segments: Segment[];
}

function compile(path: string): Segment[] {
  return path
    .split('/')
    .filter((part) => part.length > 0)
    .map((part): Segment => {
      if (!part.startsWith(':')) return { kind: 'static', value: part };
      return part.endsWith('*') ? { kind: 'rest', name: part.slice(1, -1) } : { kind: 'param', name: part.slice(1) };
    });
}

/**
 * Order so a static segment beats a param at the first position they differ (`/api/lots/batch`
 * before `/api/lots/:id`), and a catch-all comes last. Mirrors how Next resolves the route
 * folders the registry is held in parity with.
 */
function compareSpecificity(a: CompiledRoute, b: CompiledRoute): number {
  const rank = (s: Segment | undefined) => (s == null ? 3 : s.kind === 'static' ? 0 : s.kind === 'param' ? 1 : 2);
  const length = Math.max(a.segments.length, b.segments.length);
  for (let i = 0; i < length; i += 1) {
    const diff = rank(a.segments[i]) - rank(b.segments[i]);
    if (diff !== 0) return diff;
  }
  return 0;
}

function decode(part: string): string | null {
  try {
    return decodeURIComponent(part);
  } catch {
    // A malformed escape can't name any registered route.
    return null;
  }
}

function matchSegments(segments: Segment[], parts: string[]): Record<string, string> | null {
  const params: Record<string, string> = {};
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    if (segment.kind === 'rest') {
      const rest = parts.slice(i).map(decode);
      if (rest.length === 0 || rest.some((p) => p == null)) return null;
      params[segment.name] = rest.join('/');
      return params;
    }
    const part = parts[i];
    if (part == null) return null;
    const value = decode(part);
    if (value == null) return null;
    if (segment.kind === 'static') {
      if (value !== segment.value) return null;
    } else {
      params[segment.name] = value;
    }
  }
  return parts.length === segments.length ? params : null;
}

/**
 * Build a matcher over a registry tree.
 *
 * @param registry - the app's route registry.
 * @returns a function that resolves a verb and a pathname (no query string) to the entry that
 *   declares that verb at that path, or null when none does.
 */
export function createRouteMatcher(registry: RegistryTree): (input: { verb: string; pathname: string }) => RouteMatch | null {
  const routes = flattenRegistry(registry)
    .map((entry): CompiledRoute => ({ key: entry.key, verbs: new Set(entry.methods), segments: compile(entry.path) }))
    .sort(compareSpecificity);

  return ({ verb, pathname }) => {
    const upper = verb.toUpperCase() as HttpVerb;
    const parts = pathname.split('/').filter((part) => part.length > 0);
    for (const route of routes) {
      const params = matchSegments(route.segments, parts);
      if (params == null) continue;
      // The first path match is the route Next would serve; a verb it lacks is a 405, not a
      // fall-through to a less specific route.
      return route.verbs.has(upper) ? { key: route.key, verb: upper, params } : null;
    }
    return null;
  };
}
